import { Injectable } from '@angular/core';
import { Resource } from '@mode/shared/util-halogen';
import { LegacyAssetService } from '@mode/shared/contract-common';
import * as angular from 'angular';
import _ from 'lodash';
import { ModeLinkFactoryService } from '../../api/mode-link.factory';
import { ModeResourceFactoryService } from '../../api/mode-resource.factory';
import { QAdapterService } from '../../migration/q-adapter.service';
import { getHrefFromResource } from '../get-href-from-resource';
import { ModeModelBase, ModeModelInjectables } from '../model';
import { ModelAccessorBase, ModelAccessorInjectables } from '../model_accessor';
import { ReportThemeModelAccessor } from './report_theme';

export class ReportModel extends ModeModelBase {
  override _name = 'report';
  is_signed: boolean;
  secretEmbedLink: string;
  link: string;
  secretIframeLink: string;
  is_embedded: boolean;
  userStar: null;
  archived: any;
  editLink: any;
  cloneLink: string;
  share: string;
  clonable: any;
  spaceLinkable: any;
  detailsLink: string;
  embedLink: string;
  iframeLink: string;
  externalLink: any;
  signedLink: string;
  signedEmbedLink: string;
  signedIFrameLink: string;
  canToggleSharing: any;
  canToggleSigning: any;
  canToggleSecret: any;
  canCreateFromForm_spaceLinks: any;

  constructor(
    injectables: ModeModelInjectables,
    private reportModelAccessor: ReportModelAccessor,
    private modeLinkFactory: ModeLinkFactoryService,
    private $q: QAdapterService
  ) {
    super(injectables);
  }

  override idAttribute = 'token';
  override belongsTo = 'account';
  override parentIdAttribute = 'username';
  override hasMany = [
    { name: 'reportRuns', idAttribute: 'token' },
    { name: 'reportFilters', idAttribute: 'token' },
    { name: 'queries', idAttribute: 'token' },
    { name: 'queryRuns', idAttribute: 'token' },
    { name: 'pythonVisualizations', idAttribute: 'token' },
    { name: 'reportPins' },
    { name: 'reportSchedules' },
    { name: 'reportSubscriptions' },
    {
      name: 'emailSubscriptionMemberships',
      as: 'perspectiveEmailSubscriptionMemberships',
    },
    { name: 'slackSubscriptionMemberships' },
    { name: 'spaceLinks' },
  ];
  override hasOne = [
    { name: 'embedKey', idAttribute: 'token' },
    { name: 'pythonNotebook' },
    { name: 'reportTheme' },
    { name: 'account', as: 'creator', idAttribute: 'username' },
    { name: 'space', idAttribute: 'token' },
    { name: 'star' },
  ];
  override attributes = [
    { name: 'name', default: '' },
    { name: 'query', default: '' },
    { name: 'layout', default: '' },
    { name: 'description', default: '' },
    { name: 'token', default: null },
    { name: 'id', default: null },
    { name: 'share', default: '' },
    { name: 'created_at', default: null },
    { name: 'edited_at', default: null },
    { name: 'updated_at', default: null },
    { name: 'last_run_at', default: null },
    { name: 'last_successfully_run_at', default: null },
    { name: 'table_view', default: {}, type: 'text' },
    { name: 'color_mappings', default: {}, type: 'text' },
    { name: 'dataSourceId', default: null },
    { name: 'account_id', default: null },
    { name: 'theme_id', default: null },
    { name: 'public', default: true },
    { name: 'link', default: '' },
    { name: 'github_link', default: null, type: 'text' },
    { name: 'embedLink', default: '' },
    { name: 'iframeLink', default: '' },
    { name: 'finishedPublishing', default: false },
    { name: 'space_token', default: null },
    { name: 'archived', default: false },
    { name: 'recent_raw_query', default: '' },
    { name: 'run_privately', default: true },
    { name: 'web_preview_image', default: null },
    { name: 'shared', default: false },
    { name: 'is_embedded', default: false },
    { name: 'is_signed', default: false },
    { name: 'full_width', default: false },
    { name: 'manual_run_disabled', default: false },
    { name: 'drill_anywhere_enabled', default: false },
    { name: 'last_successful_run_token', default: '' },
    { name: 'query_count', default: '' },
    { name: 'chart_count', default: '' },
    { name: 'schedules_count', default: '' },
    { name: 'runs_count', default: '' },
    { name: 'view_count', default: '' },
    { name: 'type', default: '' },
  ];
  override initialize(resource) {
    this.editLink = getHrefFromResource(resource, 'web_edit');
    this.cloneLink = this.share + '/clone';

    this.clonable = resource && resource.hasForm && resource.hasForm('clone');

    this.spaceLinkable = this.canCreateFromForm_spaceLinks();

    const domain = location.protocol + '//' + document.domain + (location.port ? ':' + location.port : '');
    this.link = getHrefFromResource(resource, 'web');
    this.detailsLink = getHrefFromResource(resource, 'web_details');
    this.embedLink =
      '<a href="' +
      this.link +
      '/embed" class="mode-embed">Mode Analytics</a><script src="' +
      domain +
      '/embed/embed.js"></script>';
    this.iframeLink = '<iframe src="' + this.link + '/embed" width="100%" height="300" frameborder="0"></iframe>';
    this.externalLink = getHrefFromResource(resource, 'web_external_url');

    this.signedLink =
      this.link + '/embed?access_key=[xxx]&max_age=[xxxx in seconds]&param_[foo=bar]&timestamp=[xxx]&signature=[xxx]';
    this.signedEmbedLink =
      '<a href="' +
      this.signedLink +
      '" class="mode-embed">Mode Analytics</a><script src="' +
      domain +
      '/embed/embed.js"></script>';
    this.signedIFrameLink = '<iframe src="' + this.signedLink + '" width="100%" height="300" frameborder="0"></iframe>';

    this.canToggleSharing = resource && resource.hasForm && resource.hasForm('enable_external_sharing');
    this.canToggleSigning = resource && resource.hasForm && resource.hasForm('enable_signed_embeds');
    this.canToggleSecret = resource && resource.hasEmbedded && resource.hasEmbedded('new_embed_key');
  }
  toggleSignedEmbed(signed) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const form = signed ? 'disable_signed_embeds' : 'enable_signed_embeds';

    return this._resource
      .forms(form)
      .$submit()
      .then((res) => {
        return res;
      })
      .catch(() => {
        self.is_signed = !self.is_signed;
      });
  }
  initEmbedKey(embedKey) {
    const domain = location.protocol + '//' + document.domain + (location.port ? ':' + location.port : '');
    this.secretEmbedLink =
      '<a href="' +
      this.link +
      '/embed?secret_key=' +
      embedKey.value +
      '" class="mode-embed">Mode Analytics</a><script src="' +
      domain +
      '/embed/embed.js"></script>';
    this.secretIframeLink =
      '<iframe src="' +
      this.link +
      '/embed?secret_key=' +
      embedKey.value +
      '" width="100%" height="300" frameborder="0"></iframe>';
    this.is_embedded = true;

    this._resource._links['embed_key'] = this.modeLinkFactory.create(Resource, {
      href: embedKey._resource.links('self').href,
    });
  }
  cleanupEmbedKey() {
    this.secretEmbedLink = null;
    this.secretIframeLink = null;
    this.is_embedded = false;

    this._resource._links['embed_key'] = null;
  }
  isFromSpace(space) {
    return (
      space &&
      space._resource &&
      this._resource.hasLink('space') &&
      this._resource.links('space').href === space._resource.links('self').href
    );
  }
  removeStarredUse(starLink) {
    this.userStar = null;
    // remove any link or embedded reference locally
    if (this._resource) {
      this._resource._links.star = null;
      this._resource._embedded.star = null;
    }
    // remove and star collections locally
    if (this._collections) {
      _.remove(this._collections, (collection: any) => {
        return (collection.id = starLink);
      });
    }
  }

  toggleArchived() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    if (this.archived && this._resource.hasForm('unarchive')) {
      return this._resource
        .forms('unarchive')
        .$submit()
        .then(() => {
          self.archived = false;
          return self;
        });
    } else if (!this.archived && this._resource.hasForm('archive')) {
      return this._resource
        .forms('archive')
        .$submit()
        .then(() => {
          self.archived = true;
          return self;
        });
    } else {
      return this.$q.reject(new Error('unable to toggle archive'));
    }
  }

  saveReportSettings(settings) {
    return this._resource
      .forms('update_settings')
      .$submit(settings)
      .then((savedReport) => {
        return this.reportModelAccessor.putLocal(this.reportModelAccessor.createModel(savedReport));
      })
      .then(this.reportModelAccessor.buildTheme.bind(this.reportModelAccessor));
  }
}

function buildSpaceCreator(space, report) {
  return space.creator().then((creator) => {
    report.localSpaceCreator = creator;
    return report;
  });
}

function buildLocalSpace(report, space) {
  report.localSpace = space;
  if (space.private) {
    return buildSpaceCreator(space, report);
  } else {
    return report;
  }
}

function buildSpace(space, report) {
  if (!report.localSpace && !report.isFromSpace(space)) {
    if (report._resource.hasEmbedded('space')) {
      // just use embedded space
      return report.space().then(angular.bind(this, buildLocalSpace, report));
    } else {
      // grab space with space creator if no space embedded
      return report.space({ includes: 'creator' }).then(angular.bind(this, buildLocalSpace, report));
    }
  } else {
    return report;
  }
}

@Injectable({ providedIn: 'root' })
export class ReportModelAccessor extends ModelAccessorBase<ReportModel> {
  override name = 'report';
  override idAttribute = 'token';
  override parentModel = 'account';
  override parentIdAttribute = 'username';
  override createAttributes = ['account'];

  constructor(
    injectables: ModelAccessorInjectables,
    private modeLinkFactory: ModeLinkFactoryService,
    private modeResourceFactory: ModeResourceFactoryService,
    private reportThemeModel: ReportThemeModelAccessor,
    private assetService: LegacyAssetService,
    private $q: QAdapterService
  ) {
    super(injectables, 'migrated', 'mode.report');
  }

  buildTheme(report) {
    return report.reportTheme().then(
      (reportTheme) => {
        report.localTheme = reportTheme;
        return report;
      },
      (e) => {
        // FIXME: remove this workaround once backend is fixed to pass in theme to Secret Embeds and External Sharing pages
        console.error('Theme not embedded in report model response:', e);
        report.localTheme = this.reportThemeModel.createModel(
          this.modeResourceFactory.create({
            css_href: this.assetService.embedReportCSS,
          })
        );
        return report;
      }
    );
  }

  buildSpacesAsyncInSpace(space, reports) {
    _.forEach(reports, <any>angular.bind(this, buildSpace, space));
    return reports;
  }
  createModel(resource, opts?) {
    const model = new ReportModel(this.injectables.modelInjectables, this, this.modeLinkFactory, this.$q);
    model.$initialize(resource, opts);
    return model;
  }
}
